import React, { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Save, User, Shield, Info, Camera } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useQuery } from "@tanstack/react-query";
import { ContributorManagement } from "@/components/settings/ContributorManagement";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useFileUpload } from "@/hooks/notifications/useFileUpload";
import { useModuleAccess } from "@/hooks/useModuleAccess";
import { useLanguage } from "@/contexts/LanguageContext";
import { Eye, Building2, Calendar, FileText, Users, MapPin, Activity, AlertTriangle, Package, ShieldCheck } from "lucide-react";

interface ProfileData {
  id: string;
  email: string;
  full_name: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  mobile_phone: string | null;
  address: string | null;
  date_of_birth: string | null;
  afm: string | null;
  identity_number: string | null;
  emergency_contact: string | null;
  avatar_url: string | null;
}

export const AccountSettings = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const { isAdmin, isSuperAdmin, isContributor } = useUserRoles();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const { uploadFile } = useFileUpload();
  const { isEnabled, isLoading: modulesLoading } = useModuleAccess();

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: t('error'),
          description: t('userNotFound'),
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;

      setProfile({
        ...data,
        email: user.email || '',
      });
      setUserId(user.id);
    } catch (error: any) {
      console.error('Error fetching profile:', error);
      toast({
        title: t('error'),
        description: error.message || t('failedToLoadProfile'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!profile) return;

    try {
      setSaving(true);

      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: profile.full_name,
          first_name: profile.first_name,
          last_name: profile.last_name,
          phone: profile.phone,
          mobile_phone: profile.mobile_phone,
          address: profile.address,
          date_of_birth: profile.date_of_birth,
          afm: profile.afm,
          identity_number: profile.identity_number,
          emergency_contact: profile.emergency_contact,
        })
        .eq('id', profile.id);

      if (error) throw error;

      toast({
        title: t('success'),
        description: t('profileUpdatedSuccessfully'),
      });
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast({
        title: t('error'),
        description: error.message || t('failedToUpdateProfile'),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof ProfileData, value: string) => {
    if (!profile) return;
    setProfile({ ...profile, [field]: value });
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0 || !profile) return;
    const file = e.target.files[0];
    setUploadingAvatar(true);
    try {
      const ext = file.name.split('.').pop();
      const publicUrl = await uploadFile(file, 'avatars', `${profile.id}/${Date.now()}.${ext}`);
      if (!publicUrl) throw new Error('Failed to upload avatar');
      
      const { error } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', profile.id);
      
      if (error) throw error;
      
      setProfile({ ...profile, avatar_url: publicUrl });
      toast({ title: t('avatarUpdatedSuccessfully') });
    } catch (err: any) {
      toast({ 
        variant: 'destructive', 
        title: t('uploadFailed'), 
        description: err.message || t('couldNotUploadAvatar') 
      });
    } finally {
      setUploadingAvatar(false);
      e.currentTarget.value = '';
    }
  };

  const handleRemoveAvatar = async () => {
    if (!profile) return;
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ avatar_url: null })
        .eq('id', profile.id);
      
      if (error) throw error;
      
      setProfile({ ...profile, avatar_url: null });
      toast({ title: t('avatarRemovedSuccessfully') });
    } catch (err: any) {
      toast({ 
        variant: 'destructive', 
        title: t('removeFailed'), 
        description: err.message || t('couldNotRemoveAvatar') 
      });
    }
  };

  // Fetch admin limits (for regular admins to view their own limits)
  const { data: adminLimits } = useQuery({
    queryKey: ["adminLimits", userId],
    queryFn: async () => {
      if (!userId) return null;
      
      // Get parent admin ID (if this user is a contributor)
      const { data: parentData } = await supabase.rpc('get_parent_admin', { user_id: userId });
      const parentAdminId = parentData || userId;

      const { data, error } = await supabase
        .from("admin_limits")
        .select("*")
        .eq("admin_id", parentAdminId)
        .maybeSingle();
      
      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
    enabled: !!userId && isAdmin && !isSuperAdmin,
  });

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">{/* Account Limits Display (for non-super admins) */}
      {isAdmin && !isSuperAdmin && adminLimits && (
        <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-blue-500/5">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <CardTitle className="text-xl">{t('accountLimits')}</CardTitle>
            </div>
            <CardDescription>
              {t('yourCurrentAccountUsageLimits')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {[
                { label: t('totalUsers'), value: adminLimits.max_users, icon: User },
                { label: t('teams'), value: adminLimits.max_teams, icon: Shield },
                { label: t('managers'), value: adminLimits.max_managers, icon: User },
                { label: t('clients'), value: adminLimits.max_clients, icon: User },
                { label: t('guards'), value: adminLimits.max_guards, icon: Shield },
              ].map((item) => (
                <div key={item.label} className="space-y-2">
                  <Label className="text-xs text-muted-foreground">{item.label}</Label>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-sm">
                      {item.value ? item.value : t('unlimited')}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
            {!adminLimits.max_users && !adminLimits.max_teams && !adminLimits.max_managers && (
              <Alert className="mt-4">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  {t('noLimitsHaveBeenSet')}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Module Access Display */}
      {!modulesLoading && (
        <Card className="border-2 border-muted/20">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-xl">{t('moduleAccess')}</CardTitle>
            </div>
            <CardDescription>
              {t('availableAndDisabledModules')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {[
                { id: 'sentinel', label: t('sentinel') },
                { id: 'facilityPresence', label: t('facilityPresence') },
                { id: 'scheduling', label: t('scheduling') },
                { id: 'hrManagement', label: t('hrManagement') },
                { id: 'missions', label: t('missions') },
                { id: 'performance', label: t('performance') },
                { id: 'training', label: t('training') },
                { id: 'whistleblowers', label: t('whistleblowers') },
                { id: 'ethicsCompliance', label: t('ethicsCompliance') },
                { id: 'equipmentManagement', label: t('equipmentManagement') },
                { id: 'supplierEvaluations', label: t('supplierEvaluations') },
              ].map((module) => (
                <div key={module.id} className="flex items-center gap-2">
                  <Badge 
                    variant={isEnabled(module.id) ? "default" : "outline"} 
                    className={`text-xs ${isEnabled(module.id) ? 'bg-green-500/10 text-green-600 border-green-500/20' : 'bg-gray-500/10 text-gray-500 border-gray-500/20'}`}
                  >
                    {isEnabled(module.id) ? t('enabled') : t('disabled')}
                  </Badge>
                  <span className="text-sm text-muted-foreground">{module.label}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
  
  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Account Limits Display (for non-super admins) */}
      {isAdmin && !isSuperAdmin && adminLimits && (
        <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-blue-500/5">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <CardTitle className="text-xl">{t('accountLimits')}</CardTitle>
            </div>
            <CardDescription>
              {t('yourCurrentAccountUsageLimits')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { label: t('totalUsers'), value: adminLimits.max_users, icon: User },
              { label: t('teams'), value: adminLimits.max_teams, icon: Shield },
              { label: t('managers'), value: adminLimits.max_managers, icon: User },
              { label: t('clients'), value: adminLimits.max_clients, icon: User },
              { label: t('guards'), value: adminLimits.max_guards, icon: Shield },
            ].map((item) => (
              <div key={item.label} className="space-y-2">
                <Label className="text-xs text-muted-foreground">{item.label}</Label>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-sm">
                    {item.value ? item.value : t('unlimited')}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
          {!adminLimits.max_users && !adminLimits.max_teams && !adminLimits.max_managers && (
            <Alert className="mt-4">
              <Info className="h-4 w-4" />
              <AlertDescription>
                {t('noLimitsHaveBeenSet')}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    )}

    {/* Module Access Status - Read-only display */}
    <Card className="border-2 border-muted/50 bg-gradient-to-br from-muted/30 to-muted/10">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          <CardTitle className="text-xl">{t('moduleAccess')}</CardTitle>
        </div>
        <CardDescription>
          {t('availableAndDisabledModules')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {modulesLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { name: t('sentinel'), key: "sentinel", icon: Eye },
              { name: t('facilityPresence'), key: "facility_presence", icon: Building2 },
              { name: t('scheduling'), key: "scheduling", icon: Calendar },
              { name: t('reports'), key: "reports", icon: FileText },
              { name: t('hrManagement'), key: "hr_management", icon: Users },
              { name: t('missions'), key: "missions", icon: MapPin },
              { name: t('performance'), key: "performance", icon: Activity },
              { name: t('training'), key: "training", icon: Users },
              { name: t('whistleblowers'), key: "whistleblowers", icon: AlertTriangle },
              { name: t('ethicsCompliance'), key: "ethics_compliance", icon: Shield },
              { name: t('equipmentManagement'), key: "equipment_management", icon: Package },
              { name: t('supplierEvaluations'), key: "supplier_evaluations", icon: ShieldCheck },
            ].map((module) => {
              const moduleEnabled = isEnabled(module.key);
              const Icon = module.icon;
              return (
                <div
                  key={module.key}
                  className={`p-4 rounded-lg border transition-all ${
                    moduleEnabled
                      ? "bg-primary/5 border-primary/20"
                      : "bg-muted/30 border-muted"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 rounded-lg ${
                        moduleEnabled ? "bg-primary/10" : "bg-muted"
                      }`}
                    >
                      <Icon
                        className={`h-4 w-4 ${
                          moduleEnabled ? "text-primary" : "text-muted-foreground"
                        }`}
                      />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-sm">{module.name}</h4>
                      <Badge
                        variant={moduleEnabled ? "default" : "secondary"}
                        className="mt-1 text-xs"
                      >
                        {moduleEnabled ? t('enabled') : t('disabled')}
                      </Badge>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
      </Card>

      {/* Contributors Management (for non-super admins and non-contributors) */}
      {isAdmin && !isSuperAdmin && !isContributor && <ContributorManagement />}

      {/* Profile Information Card */}
    <Card className="border-0 shadow-none p-5">
      <CardHeader className="px-0">
        <div className="flex items-center space-x-3 mb-2">
          <div className="p-2 bg-gradient-to-br from-primary/10 to-blue-500/10 rounded-xl">
            <User className="h-5 w-5 text-primary" />
          </div>
          <CardTitle className="text-2xl">{t('profileInformation')}</CardTitle>
        </div>
        <CardDescription className="text-base">
          {t('updateYourPersonalInformation')}
        </CardDescription>
      </CardHeader>

      <Separator className="my-6" />

      <CardContent className="px-0">
        <div className="space-y-8">
          {/* Avatar Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">{t('profilePicture')}</h3>
            <div className="flex items-center gap-6">
              <Avatar className="h-24 w-24 ring-2 ring-border">
                <AvatarImage 
                  src={profile?.avatar_url || undefined} 
                  alt={profile?.full_name || 'User'} 
                />
                <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                  {profile?.first_name?.[0] || profile?.full_name?.[0] || 'U'}
                  {profile?.last_name?.[0] || ''}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col gap-2">
                <label htmlFor="avatar-upload">
                  <input 
                    id="avatar-upload" 
                    type="file" 
                    accept="image/*" 
                    className="hidden" 
                    onChange={handleAvatarChange}
                    disabled={uploadingAvatar || saving}
                  />
                  <Button 
                    type="button" 
                    variant="secondary" 
                    disabled={uploadingAvatar || saving}
                    asChild
                  >
                    <span>
                      <Camera className="h-4 w-4 mr-2" />
                      {uploadingAvatar ? t('uploading') : t('changePhoto')}
                    </span>
                  </Button>
                </label>
                {profile?.avatar_url && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    disabled={uploadingAvatar || saving} 
                    onClick={handleRemoveAvatar}
                  >
                    {t('removePhoto')}
                  </Button>
                )}
                <p className="text-xs text-muted-foreground">
                  {t('recommendedSquareImage')}
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">{t('basicInformation')}</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">{t('email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={profile?.email || ''}
                  disabled
                  className="bg-muted/50"
                />
                <p className="text-xs text-muted-foreground">{t('emailCannotBeChanged')}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="full_name" className="text-sm font-medium">{t('fullName')}</Label>
                <Input
                  id="full_name"
                  value={profile?.full_name || ''}
                  onChange={(e) => updateField('full_name', e.target.value)}
                  placeholder={t('enterYourFullName')}
                  className="transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-sm font-medium">{t('firstName')}</Label>
                <Input
                  id="first_name"
                  value={profile?.first_name || ''}
                  onChange={(e) => updateField('first_name', e.target.value)}
                  placeholder={t('enterFirstName')}
                  className="transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-sm font-medium">{t('lastName')}</Label>
                <Input
                  id="last_name"
                  value={profile?.last_name || ''}
                  onChange={(e) => updateField('last_name', e.target.value)}
                  placeholder={t('enterLastName')}
                  className="transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="date_of_birth" className="text-sm font-medium">{t('dateOfBirth')}</Label>
                <Input
                  id="date_of_birth"
                  type="date"
                  value={profile?.date_of_birth || ''}
                  onChange={(e) => updateField('date_of_birth', e.target.value)}
                  className="transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">{t('contactInformation')}</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium">{t('phone')}</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={profile?.phone || ''}
                  onChange={(e) => updateField('phone', e.target.value)}
                  placeholder={t('enterPhoneNumber')}
                  className="transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="mobile_phone" className="text-sm font-medium">{t('mobilePhone')}</Label>
                <Input
                  id="mobile_phone"
                  type="tel"
                  value={profile?.mobile_phone || ''}
                  onChange={(e) => updateField('mobile_phone', e.target.value)}
                  placeholder={t('enterMobilePhoneNumber')}
                  className="transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="address" className="text-sm font-medium">{t('address')}</Label>
                <Input
                  id="address"
                  value={profile?.address || ''}
                  onChange={(e) => updateField('address', e.target.value)}
                  placeholder={t('enterYourAddress')}
                  className="transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="emergency_contact" className="text-sm font-medium">{t('emergencyContact')}</Label>
                <Input
                  id="emergency_contact"
                  type="tel"
                  value={profile?.emergency_contact || ''}
                  onChange={(e) => updateField('emergency_contact', e.target.value)}
                  placeholder={t('emergencyContactNumber')}
                  className="transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Additional Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">{t('additionalInformation')}</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="afm" className="text-sm font-medium">{t('afmTaxId')}</Label>
                <Input
                  id="afm"
                  value={profile?.afm || ''}
                  onChange={(e) => updateField('afm', e.target.value)}
                  placeholder={t('enterAFM')}
                  className="transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="identity_number" className="text-sm font-medium">{t('identityNumber')}</Label>
                <Input
                  id="identity_number"
                  value={profile?.identity_number || ''}
                  onChange={(e) => updateField('identity_number', e.target.value)}
                  placeholder={t('enterIdentityNumber')}
                  className="transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Save Button */}
          <div className="flex justify-end pt-4">
            <Button 
              onClick={handleSave} 
              disabled={saving}
              size="lg"
              className="px-8 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('saving')}...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {t('saveChanges')}
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
    </div>
  );
};